import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, DollarSign, Star, TrendingUp, Share2 } from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';
import { BookAgainSection } from '@/components/BookAgainSection';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import GoldenScissors from '@/components/GoldenScissors';

const RATED_KEY = 'onda_rated_bookings';
const getRated = () => { try { return JSON.parse(localStorage.getItem(RATED_KEY) || '[]'); } catch { return []; } };
const markRated = (id) => localStorage.setItem(RATED_KEY, JSON.stringify([...getRated(), id]));

export default function ClientDashboard() {
  const [bookings, setBookings] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingFeedback, setPendingFeedback] = useState([]);
  const [ratePrompt, setRatePrompt] = useState(null); // the completed booking awaiting its "new look" moment
  const [stars, setStars] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    fetchBookings();
    fetchPendingFeedback();
  }, []);

  const fetchPendingFeedback = async () => {
    try {
      const res = await api.get('/feedback/pending');
      setPendingFeedback(res.data.pending_feedback || []);
    } catch {}
  };

  const fetchBookings = async () => {
    try {
      const response = await api.get('/bookings/my-bookings');
      setBookings(response.data);
      // The "Rate your new look!!" moment — first completed booking not yet rated.
      const rated = getRated();
      const awaiting = response.data.find(
        (b) => b.status === 'completed' && !rated.includes(b.booking_id)
      );
      if (awaiting) setRatePrompt(awaiting);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const submitRating = async () => {
    if (!stars) { toast.error('Tap the stars to rate your look'); return; }
    setSubmittingRating(true);
    try {
      await api.post('/ratings/', { booking_id: ratePrompt.booking_id, rating: stars, feedback: feedback || null });
      toast.success('Thank you — your stylist just earned your hearts! 💛');
    } catch (e) {
      // Already rated / any failure — don't trap the user in the dialog.
      const detail = e.response?.data?.detail;
      if (typeof detail === 'string' && detail.includes('already')) toast.info('This booking was already rated');
      else toast.error(typeof detail === 'string' ? detail : 'Could not submit the rating');
    } finally {
      markRated(ratePrompt.booking_id);
      setSubmittingRating(false);
      setRatePrompt(null);
      setStars(0);
      setFeedback('');
    }
  };

  const shareNewLook = async () => {
    const url = `https://onda.ivonharris.com/stylist/${ratePrompt?.stylist_id || ''}`;
    const text = `Fresh new look thanks to my Onda stylist! ✂️✨ Book yours — beauty that comes to you.`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'My new look — Onda', text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success('Caption copied — paste it into Insta/TikTok!');
      }
    } catch (e) { /* user closed the share sheet */ }
  };

  const stats = [
    {
      title: 'Total Bookings',
      value: bookings.length,
      icon: <Calendar className="h-8 w-8 text-secondary" />,
    },
    {
      title: 'Upcoming',
      value: bookings.filter(b => ['pending', 'confirmed', 'accepted'].includes(b.status)).length,
      icon: <TrendingUp className="h-8 w-8 text-secondary" />,
    },
    {
      title: 'Completed',
      value: bookings.filter(b => b.status === 'completed').length,
      icon: <Star className="h-8 w-8 text-secondary" />,
    },
  ];

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="container mx-auto max-w-7xl">
        <div className="mb-12" data-testid="client-dashboard-header">
          <h1 className="font-cormorant text-6xl font-bold mb-2 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Welcome back, {user?.first_name || user?.name}</h1>
          <p className="text-lg text-muted-foreground font-montserrat">Manage your bookings and discover new stylists</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12" data-testid="stats-grid">
          {stats.map((stat, idx) => (
            <Card key={idx} className="luxury-card border-border/40">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                    <p className="text-4xl font-bold font-cormorant text-primary">{stat.value}</p>
                  </div>
                  {stat.icon}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pending Feedback Banner */}
        {pendingFeedback.length > 0 && (
          <div className="mb-8 space-y-3">
            {pendingFeedback.map(fb => (
              <div key={fb.feedback_id} className="bg-gradient-to-r from-primary/15 to-amber-500/10 border border-primary/30 rounded-xl p-5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-3xl">✨</div>
                  <div>
                    <p className="text-foreground font-semibold">Rate your experience with {fb.stylist_name}</p>
                    <p className="text-sm text-gray-400">Share your feedback and earn a 15% discount coupon!</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/rate/${fb.booking_id}`)}
                  className="px-5 py-2.5 bg-primary text-primary-foreground rounded-xl font-medium hover:opacity-90 transition whitespace-nowrap"
                >
                  Rate Now
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Book Again Section */}
        <div className="mb-12">
          <BookAgainSection limit={4} showTitle={true} />
        </div>

        <Card className="luxury-card border-border/40" data-testid="bookings-card">
          <CardHeader>
            <CardTitle className="font-cormorant text-3xl">All Bookings</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8 text-muted-foreground">Loading bookings...</p>
            ) : bookings.length > 0 ? (
              <div className="space-y-4">
                {bookings.map(booking => (
                  <div key={booking.booking_id} className="flex items-center justify-between p-4 border border-border/40 rounded-lg hover:border-primary/30 transition-colors" data-testid={`booking-item-${booking.booking_id}`}>
                    <div>
                      <p className="font-semibold text-lg">{booking.stylist_name}</p>
                      <p className="text-sm text-muted-foreground">{new Date(booking.scheduled_datetime).toLocaleString()}</p>
                      <div className="flex gap-2 mt-2">
                        {booking.services?.map((service, idx) => (
                          <Badge key={idx} variant="outline" className="capitalize border-primary/30">
                            {service.replace('_', ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <p className="text-2xl font-bold text-primary">${booking.total_price?.toFixed(2)}</p>
                      <Badge
                        className={`${
                          booking.status === 'completed' ? 'bg-green-500' :
                          booking.status === 'confirmed' ? 'bg-blue-500' :
                          booking.status === 'cancelled' ? 'bg-red-500' :
                          'bg-yellow-500'
                        }`}
                        data-testid={`booking-status-${booking.booking_id}`}
                      >
                        {booking.status}
                      </Badge>
                      {booking.status === 'completed' && pendingFeedback.some(f => f.booking_id === booking.booking_id) && (
                        <button
                          onClick={() => navigate(`/rate/${booking.booking_id}`)}
                          className="text-xs px-3 py-1 bg-primary/20 text-primary rounded-full hover:bg-primary/30 transition font-medium"
                        >
                          Rate Experience ✨
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">No bookings yet</p>
                <a href="/services" className="text-primary font-semibold hover:underline">Browse services to get started</a>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!ratePrompt} onOpenChange={(open) => { if (!open) { markRated(ratePrompt?.booking_id); setRatePrompt(null); } }}>
        <DialogContent className="max-w-md text-center">
          <DialogHeader>
            <div className="mx-auto mb-1"><GoldenScissors size="md" /></div>
            <DialogTitle className="font-cormorant text-3xl text-primary text-center">
              Rate your new look!!
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            How did {ratePrompt?.stylist_name || 'your stylist'} do? Your rating grows their hearts.
          </p>
          <div className="flex justify-center gap-2 py-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setStars(n)} aria-label={`${n} stars`}
                className="transition-transform hover:scale-110">
                <Star className={`h-9 w-9 ${n <= stars ? 'text-primary fill-primary' : 'text-muted-foreground/40'}`} />
              </button>
            ))}
          </div>
          <Textarea placeholder="Say something lovely (optional)…" rows={2} maxLength={500}
            value={feedback} onChange={(e) => setFeedback(e.target.value)} />
          <div className="flex flex-col gap-2 pt-1">
            <Button className="btn-primary w-full" onClick={submitRating} disabled={submittingRating}>
              {submittingRating ? <GoldenScissors size="sm" className="mr-2" /> : null}
              Submit rating
            </Button>
            <Button variant="outline" className="w-full" onClick={shareNewLook}>
              <Share2 className="mr-2 h-4 w-4" /> Show off my new look
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
